/**
 * POLAR tracking additional-angle LIST preferences (AutoCAD `POLARADDANG`
 * string). The "use additional angles" ON/OFF flag is NOT here — it is
 * POLARMODE bit 4 in the sysvar store (`selectPolarAddAngEnabled`), so it
 * round-trips through `SETVAR POLARMODE` / query (#492 @codex P2). This store owns
 * only the string-typed angle list that cannot live in the numeric sysvar schema.
 *
 * # Why a separate store
 *
 * `POLARADDANG` is a STRING system variable in AutoCAD — a semicolon-delimited
 * list of up to ten additional tracking angles ("15;22.5;72"). The AutoCAD-parity
 * `sysvarStore` (`apps/web/src/app/dynamicInput/sysvarStore.ts`) is strictly
 * integer/numeric by contract (DYNMODE / OSMODE / POLARANG …), so a string list
 * cannot live there without diluting its `SETVAR` numeric parity. Following the
 * same rationale as `rendererPreference.ts` / `previewPreferences.ts`, the
 * additional-angle list lives in this tiny persisted zustand store instead.
 *
 * The list is projected into the AUTHORITATIVE typed {@link PolarTrackingConfig}
 * (`scene3d/snap/polarConstraint.ts`) by the polar drafting-lock gate — the CAD
 * semantics live in the typed core, this store is only the editable source of the
 * user-supplied additional angles (CLAUDE.md #1: typed model first, UI projection
 * after). `POLARANG` (the primary increment, numeric) stays a real sysvar.
 *
 * N6 (POLAR-RELATIVE-BASIS-WIRE) — the measurement basis (absolute UCS vs
 * relative to last segment) used to have a same-named `angleBasis` field
 * here that nothing ever wrote (a dead, never-wired boolean-equivalent —
 * exactly the anti-pattern POLARADDANG's own enable bit was migrated OUT of
 * this store to avoid, per the paragraph above). It is now a numeric
 * POLARMODE bit (`POLARMODE_RELATIVE_BASIS_BIT` /
 * `selectPolarAngleBasis` in `dynamicInput/sysvarStore.ts`), the single
 * source of truth `polarDraftLock.ts` / `polarGuide.ts` read — this store no
 * longer carries a basis field at all.
 *
 * References:
 *   - Autodesk: POLARADDANG (System Variable) — "Contains a list of user-defined
 *     polar angles. ... You can add up to 10 angles."
 */

import { create } from "zustand";

import { MAX_POLAR_ADD_ANGLES } from "../scene3d/snap/polarConstraint";

export interface PolarTrackingPrefs {
  /** POLARADDANG — user-defined additional tracking angles in degrees [0, 360). */
  additionalAnglesDeg: number[];
}

export const DEFAULT_POLAR_TRACKING_PREFS: PolarTrackingPrefs = {
  additionalAnglesDeg: [],
};

const LOCAL_STORAGE_KEY = "edit-dwg.polar-tracking";

/** Normalise an angle to [0, 360). */
function norm360(deg: number): number {
  const m = deg % 360;
  return m < 0 ? m + 360 : m;
}

/**
 * Parse a POLARADDANG-style string ("15;22.5;72" — semicolon OR comma
 * separated) into a normalised, de-duplicated, capped list of angles in degrees.
 * Non-finite / out-of-range tokens are dropped (AutoCAD ignores malformed
 * entries rather than rejecting the whole list).
 */
export function parsePolarAddAng(text: string): number[] {
  const out: number[] = [];
  const seen = new Set<number>();
  for (const raw of text.split(/[;,]/)) {
    const token = raw.trim();
    if (!token) continue;
    const value = Number(token);
    if (!Number.isFinite(value)) continue;
    // POLARADDANG angles are contracted to [0, 360) (see PolarTrackingPrefs).
    // AutoCAD DROPS out-of-range tokens rather than wrapping them — wrapping a
    // `-30` up to `330` would silently store a DIFFERENT tracking ray than the
    // user typed and let it participate once "Additional angles" is enabled.
    // Reject out-of-range so only genuine in-range angles are kept (#492 @codex P3).
    if (value < 0 || value >= 360) continue;
    const normalised = Math.round(norm360(value) * 1e6) / 1e6;
    if (seen.has(normalised)) continue;
    seen.add(normalised);
    out.push(normalised);
    if (out.length >= MAX_POLAR_ADD_ANGLES) break;
  }
  return out;
}

/** Format an additional-angle list back to the POLARADDANG "a;b;c" string. */
export function formatPolarAddAng(list: readonly number[]): string {
  return list.join(";");
}

export function readPersistedPolarTrackingPrefs(): PolarTrackingPrefs {
  if (typeof window === "undefined") {
    return { ...DEFAULT_POLAR_TRACKING_PREFS, additionalAnglesDeg: [] };
  }
  try {
    const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!stored) {
      return { ...DEFAULT_POLAR_TRACKING_PREFS, additionalAnglesDeg: [] };
    }
    const parsed: unknown = JSON.parse(stored);
    if (!parsed || typeof parsed !== "object") {
      return { ...DEFAULT_POLAR_TRACKING_PREFS, additionalAnglesDeg: [] };
    }
    const candidate = parsed as Partial<PolarTrackingPrefs>;
    const angles = Array.isArray(candidate.additionalAnglesDeg)
      ? candidate.additionalAnglesDeg
          .filter((a): a is number => typeof a === "number" && Number.isFinite(a))
          .map((a) => Math.round(norm360(a) * 1e6) / 1e6)
          .slice(0, MAX_POLAR_ADD_ANGLES)
      : [];
    return { additionalAnglesDeg: angles };
  } catch {
    return { ...DEFAULT_POLAR_TRACKING_PREFS, additionalAnglesDeg: [] };
  }
}

function persistPolarTrackingPrefs(prefs: PolarTrackingPrefs): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage may be unavailable (private mode / quota) — ignore.
  }
}

interface PolarTrackingPrefsStore extends PolarTrackingPrefs {
  /** Replace the additional-angle list (already parsed / normalised). */
  setAdditionalAngles: (angles: readonly number[]) => void;
  reset: () => void;
}

function snapshot(state: PolarTrackingPrefs): PolarTrackingPrefs {
  return {
    additionalAnglesDeg: [...state.additionalAnglesDeg],
  };
}

/**
 * Zustand store — usable both as a React hook (`usePolarTrackingPrefs(sel)`) and
 * imperatively (`usePolarTrackingPrefs.getState()`), mirroring `useSysvarStore`.
 * The drafting-lock gate reads `getState()` at event time.
 */
export const usePolarTrackingPrefs = create<PolarTrackingPrefsStore>(
  (set, get) => ({
    ...readPersistedPolarTrackingPrefs(),
    setAdditionalAngles: (angles) => {
      set({ additionalAnglesDeg: angles.slice(0, MAX_POLAR_ADD_ANGLES) });
      persistPolarTrackingPrefs(snapshot(get()));
    },
    reset: () => {
      const fresh: PolarTrackingPrefs = {
        ...DEFAULT_POLAR_TRACKING_PREFS,
        additionalAnglesDeg: [],
      };
      set(fresh);
      persistPolarTrackingPrefs(fresh);
    },
  }),
);

/** Pure getter for non-React callers that only need a data snapshot. */
export function getPolarTrackingPrefs(): PolarTrackingPrefs {
  return snapshot(usePolarTrackingPrefs.getState());
}
